import { useEffect, useRef } from 'react'
import pick from 'lodash/pick'
import { useTranslation as useTranslationNext } from 'next-i18next'
import type { UseTranslationResponse } from 'react-i18next'
import { useLocale } from '@/utils/LocaleUtil'
import { logger } from '@/utils/logging'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { isDevelopmentEnvironment } from '../environment-utils'

export type TranslationNamespace =
  | 'about'
  | 'account'
  | 'ads'
  | 'ambassador'
  | 'app-promo'
  | 'auth'
  | 'category'
  | 'chatbot'
  | 'collectibles'
  | 'collectibles-marketplace'
  | 'coming-soon'
  | 'common'
  | 'create'
  | 'early-access'
  | 'features'
  | 'feedback'
  | 'filmmaker'
  | 'giveaway'
  | 'guild'
  | 'guild-benefits'
  | 'guild-create'
  | 'guild-merch'
  | 'guild-voting'
  | 'marketing-gift'
  | 'home'
  | 'impact'
  | 'join-the-guild'
  | 'livestream'
  | 'music'
  | 'notification-preferences'
  | 'partnership'
  | 'pif'
  | 'pt-announcement'
  | 'reactions'
  | 'redeem'
  | 'sign-up'
  | 'sight-announcement'
  | 'theatrical-presales'
  | 'theatrical-exclusive-scenes'
  | 'the-shift-exclusive'
  | 'tickets'
  | 'tv-post-auth'
  | 'viewer-profiles'
  | 'watch'
  | 'world-tour'

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((v) => typeof v === 'string')
}

function hasDefault(value: unknown): value is { default: unknown } {
  return Boolean(value) && typeof value === 'object' && 'default' in (value as Record<string, unknown>)
}

function normalizeFallbacks(fallbackLngOption: unknown): string[] {
  if (isStringArray(fallbackLngOption)) return [...fallbackLngOption]
  if (typeof fallbackLngOption === 'string') return [fallbackLngOption]
  if (hasDefault(fallbackLngOption) && isStringArray(fallbackLngOption.default)) return [...fallbackLngOption.default]
  return []
}

function getIsInAppBrowser(userAgent: string | undefined): boolean {
  if (typeof userAgent !== 'string') return false
  return (
    userAgent.includes('FBAN') ||
    userAgent.includes('FBAV') ||
    userAgent.includes('Instagram') ||
    userAgent.includes('FB_IAB')
  )
}

function getDiagnosticInfo(ns: string | readonly string[] | undefined) {
  const userAgent = typeof window !== 'undefined' ? window.navigator?.userAgent : 'SSR'
  const referrer = typeof document !== 'undefined' ? document.referrer : 'SSR'
  return {
    loadedNamespaces: ns || [],
    userAgent,
    referrer,
    isInAppBrowser: getIsInAppBrowser(userAgent),
  }
}

export function useTranslateInternal(namespace: TranslationNamespace) {
  // Note, renaming useTranslation to useTranslationNext intentionally makes it so the i18n-parser won't parse this file.
  // By design, the i18n-parser will throw a warning, which breaks our build, when a variable is provided.
  // In this case, we really do want to pass in the namespace as a variable, because we've created the wrapper function.
  const result = useTranslationNext(namespace)
  const { pathname, asPath } = useSafeRouter()
  const { locale: pathLocale } = useLocale()

  // Validation logic: we previously only checked the locale segment from the URL (e.g. "en").
  // However, i18next may resolve a more specific language (e.g. "en-US") based on the browser,
  // and the resource bundle will then be stored under that resolved language. This caused false
  // errors because we looked for the bundle under "en" when only "en-US" was loaded.
  // We now consider multiple candidates and only log an error if none of them have the namespace.
  const alreadyLoggedRef = useRef(false)

  useEffect(() => {
    // Only attempt validation after i18next reports initialization AND after the namespace has finished loading.
    // This prevents false positives during client-side navigations where the namespace is still being fetched.
    if (!result?.i18n || !result.ready) return
    const i18n = result.i18n

    // If i18n hasn't loaded the namespace yet, skip validation – useTranslation will trigger loading.
    const hasLoadedNamespace = i18n?.hasLoadedNamespace?.(namespace)
    if (!hasLoadedNamespace) return

    const activeLanguage = i18n?.resolvedLanguage || i18n?.language
    const fallbackLngOption = i18n?.options?.fallbackLng
    const normalizedFallbacks = normalizeFallbacks(fallbackLngOption)

    const isNonEmptyString = (v: unknown): v is string => typeof v === 'string' && v.length > 0
    const candidates = Array.from(
      new Set(
        [
          pathLocale,
          activeLanguage,
          pathLocale?.split('-')[0],
          activeLanguage?.split('-')[0],
          ...normalizedFallbacks,
        ].filter(isNonEmptyString),
      ),
    )

    const hasBundle = candidates.some((lng) => {
      try {
        return Boolean(i18n?.getResourceBundle?.(lng, namespace))
      } catch {
        return false
      }
    })

    if (!hasBundle && !alreadyLoggedRef.current) {
      alreadyLoggedRef.current = true
      const currentPath = (asPath || pathname || '').split('?')[0]
      const message = `The locale "${pathLocale}" (resolved: "${activeLanguage}") for translations for this namespace "${namespace}" has not been loaded! Verify that this namespace is being loaded using the "loadTranslations" function for this page: "${currentPath}".`

      if (isDevelopmentEnvironment()) throw new Error(message)

      const diagnosticInfo = getDiagnosticInfo(i18n.options?.ns)
      logger().error(message, {
        namespace,
        pathLocale,
        activeLanguage,
        candidates,
        currentPath,
        ...diagnosticInfo,
        result: formatResultForLogs(result),
      })
    }
  }, [result, namespace, pathname, pathLocale, asPath])

  return result
}

function formatResultForLogs(result: UseTranslationResponse<TranslationNamespace, undefined>) {
  const { ready, i18n } = result
  const i18nCopy = pick(i18n, ['language', 'languages', 'resolvedLanguage', 'isInitialized'])
  const optionsCopy = pick(i18n.options, [
    'debug',
    'partialBundledLanguages',
    'lng',
    'fallbackLng',
    'supportedLngs',
    'nonExplicitSupportedLngs',
    'load',
    'preload',
    'lowerCaseLng',
    'cleanCode',
    'ns',
    'defaultNS',
    'fallbackNS',
    'saveMissing',
    'saveMissingTo',
    'saveMissingPlurals',
    'updatingMissing',
    'missingKeyNoValueFallbackToKey',
    'appendNamespaceToMissingKey',
    'simplifyPluralSuffix',
    'postProcess',
    'postProcessPassResolved',
    'returnNull',
    'returnEmptyString',
    'returnObjects',
    'returnDetails',
    'joinArrays',
    'interpolation',
    'initAsync',
    'keySeparator',
    'nsSeparator',
    'pluralSeparator',
    'contextSeparator',
    'appendNamespaceToCIMode',
    'compatibilityJSON',
    'locizeLastUsed',
    'ignoreJSONStructure',
    'maxParallelReads',
    'maxRetries',
    'retryTimeout',
  ])

  return { ready, i18n: { ...i18nCopy, options: optionsCopy } }
}
